import React from 'react';
import { connect } from 'react-redux';
import { IconCircleInfo } from '../../ui-kit/icons';

// Custom Imports
import { operations as activityTypeOperations } from '../../../state/modules/activity-types';

import { operations as incidentOperations } from '../../../state/modules/incidents';
import {
  operations as activityReportOperations,
  selectors as activityReportSelectors
} from '../../../state/modules/activity-reports';
import { operations as incidentResponseOperations } from '../../../state/modules/incident-responses';
import { operations as skillOperations } from '../../../state/modules/skills';
import { operations as membershipOperations } from '../../../state/modules/memberships';
import { operations as groupOperations } from '../../../state/modules/groups';
import { operations as taskOperations } from '../../../state/modules/tasks';
import { selectors as incidentResponseSelectors } from '../../../state/modules/incident-responses';
import { operations as userOperations } from '../../../state/modules/users';
import { operations as rankOperations } from '../../../state/modules/ranks';
import {
  overlappingAttendancesByUserId,
  activityOverlappingAttendances
} from '../../../state/modules/attendances/selectors';
import ResponsesTable from './ResponsesTable';
import AbstractActivityReportForm from '../AbstractActivityReportForm';
import {
  operations as availabilityCodeOperations,
  selectors as availabilityCodeSelectors
} from '../../../state/modules/availability-codes';

import {
  TextField,
  TypeSelector,
  AvailabilityCodeSelector,
  DescriptionField,
  PeriodSelectorField,
  NoActivityTypeWarning,
  LongActivityWarning,
  PrioritySelector,
  TaskSelector
} from '../shared/Fields';

import { Badge } from '../../ui-kit';

const AvailabilityCodeExplanation = () => (
  <p className="my-1 small">
    <IconCircleInfo /> <Badge variant="secondary">{I18n.t('optional')} </Badge>{' '}
    {I18n.t('activity_reports.availability_code_info')}
  </p>
);

const Fields = ({
  report,
  updateFields,
  eligibleActivityTypes,
  updateDatetimeFields,
  readOnlyFields,
  tasksById,
  selectAvailabilityCodeRights,
  availabilityCodes,
  availabilityCodesLoaded,
  updateAvailabilityCode
}) => (
  <>
    <TextField
      label={I18n.t('activerecord.attributes.incidents/incident_report.body')}
      name="body"
      value={report.body}
      onChange={updateFields}
      disabled={report.type === 'incident_alert'}
    />
    <TypeSelector
      eligibleTypes={eligibleActivityTypes}
      selectedTypeIds={report.activity_type_ids}
      onChange={updateFields}
    />
    <NoActivityTypeWarning report={report} />
    {selectAvailabilityCodeRights && (
      <AvailabilityCodeSelector
        loading={!availabilityCodesLoaded}
        availabilityCodes={availabilityCodes}
        onChange={updateAvailabilityCode}
        selectedCodeIds={[report.availability_code_id]}
        multi={false}
        customInfoComponent={<AvailabilityCodeExplanation />}
      />
    )}

    <PeriodSelectorField startTime={report.start_time} endTime={report.end_time} onChange={updateDatetimeFields} />
    <LongActivityWarning report={report} />
    <TextField
      label={I18n.t('activerecord.attributes.incidents/incident.external_id')}
      name="external_id"
      value={report.external_id}
      onChange={updateFields}
      disabled={(readOnlyFields || []).includes('external_id')}
    />
    <DescriptionField value={report.description} onChange={updateFields} />
    <PrioritySelector setActive={updateFields} active={report.prio} />
    <TaskSelector tasksById={tasksById} selectedTaskIds={report.task_ids} onChange={updateFields} />
  </>
);

export const IncidentReportForm = props => {
  const createNewAttendance = (membership, reportId) => ({
    membership_id: membership.id,
    user_id: membership.user_id,
    group_id: membership.group_id,
    incident_id: reportId,
    reported_status: 'unknown',
    performed_rank_id: null,
    assigned_skill_ids: []
  });

  const cancelPath = (groupId, reportId) => {
    return reportId ? `/groups/${groupId}/incidents/${reportId}` : `/groups/${groupId}/incidents`;
  };

  props = {
    ...props,
    cancelPath,
    createNewAttendance,
    // IMPORTANT: The following props are only needed to comply with tests
    // These can be removed once the tests are updated to handle a mock store
    // Because the OverlappingAttendancesWarning component can be directly
    // connected to the store
    incidentsById: props.activityReports,
    fetchIncidents: props.getActivityReport
  };

  return (
    <AbstractActivityReportForm
      {...props}
      FieldsComponent={Fields}
      AttendancesTableComponent={ResponsesTable}
      reportType="incident"
    />
  );
};

const mapStateToProps = state => ({
  activityReports: state.incidents.byId,
  activityReportAttendances: incidentResponseSelectors.augmentedAttendances(state),
  activityTypes: state.activityTypes.byId,
  groups: state.groups,
  tasksById: state.tasks.byId,
  ranksById: state.ranks.byId,
  activitiesById: activityReportSelectors.byId(state),
  overlappingAttendancesByUserId: overlappingAttendancesByUserId(state),
  activityOverlappingAttendances: activityOverlappingAttendances(state),
  availabilityCodes: availabilityCodeSelectors.selectForActivityReport(state),
  availabilityCodesLoaded: availabilityCodeSelectors.initialLoadComplete(state)
});

const mapDispatchToProps = {
  getActivityReport: incidentOperations.fetch,
  createActivityReport: incidentOperations.createNew,
  storeActivityReport: incidentOperations.storeInBackend,
  updateReportFields: incidentOperations.updateFields,
  createActivityReportAttendances: incidentResponseOperations.createNewInBulk,
  storeActivityReportAttendance: incidentResponseOperations.storeInBackend,
  updateAttendanceFields: incidentResponseOperations.updateFields,
  fetchActivityTypes: activityTypeOperations.fetch,
  fetchMemberships: membershipOperations.fetch,
  fetchUsers: userOperations.fetch,
  fetchGroups: groupOperations.fetch,
  fetchTasks: taskOperations.fetch,
  fetchSkills: skillOperations.fetch,
  fetchRanks: rankOperations.fetch,
  fetchActivities: activityReportOperations.fetch,
  fetchAvailabilityCodes: availabilityCodeOperations.fetch
};

export default connect(mapStateToProps, mapDispatchToProps)(IncidentReportForm);
